<template>
  <a-card
      class="reference-parameter-card"
      :class="cardClasses"
      :bordered="true"
  >
    <div class="reference-parameter-card__head">
      <span class="reference-parameter-card__title">
        {{ card.title }}
      </span>

      <a-popover
          v-if="card.descriptionContent || card.descriptionFooter"
          placement="topRight"
          trigger="hover"
      >
        <template #content>
          <div class="reference-parameter-card__popover">
            <p v-if="card.descriptionContent">
              {{ card.descriptionContent }}
            </p>

            <p v-if="card.descriptionFooter">
              {{ card.descriptionFooter }}
            </p>
          </div>
        </template>

        <QuestionCircleOutlined class="reference-parameter-card__info" />
      </a-popover>

      <QuestionCircleOutlined
          v-else
          class="reference-parameter-card__info reference-parameter-card__info_empty"
      />
    </div>

    <div class="reference-parameter-card__range">
      {{ displayRange }}
    </div>
  </a-card>
</template>

<script>
import { computed } from "vue";
import { QuestionCircleOutlined } from "@ant-design/icons-vue";

const DOMAIN_CLASSES = ["experienced", "strategic"];

const BRANCH_CLASSES = [
  "recognition",
  "using",
  "understanding",
  "management",
];

const SECTION_CLASSES = [
  "faces",
  "facilitation",
  "changes",
  "managingYour",
  "images",
  "empathy",
  "mixing",
  "influencing",
  "situations",
  "dynamics",
];

export default {
  name: "ReferenceParameterCard",

  components: {
    QuestionCircleOutlined,
  },

  props: {
    card: {
      type: Object,
      required: true,
    },
  },

  setup(props) {
    function intersects(values, expectedValues) {
      return values.some((value) => expectedValues.includes(value));
    }

    const displayRange = computed(() => {
      const min = props.card?.min;
      const max = props.card?.max;

      if (min === null || min === undefined || max === null || max === undefined) {
        return "-";
      }

      return `${min}-${max}`;
    });

    const structureClasses = computed(() => {
      const value = props.card?.structureClass;

      if (Array.isArray(value)) {
        return value;
      }

      if (typeof value === "string") {
        return [value];
      }

      return [];
    });

    const isGeneral = computed(() => {
      return structureClasses.value.includes("general");
    });

    const isExperienced = computed(() => {
      return (
          structureClasses.value.includes("experienced") ||
          intersects(structureClasses.value, [
            "recognition",
            "using",
            "faces",
            "facilitation",
            "images",
            "empathy",
            "situations",
          ])
      );
    });

    const isStrategic = computed(() => {
      return (
          structureClasses.value.includes("strategic") ||
          intersects(structureClasses.value, [
            "understanding",
            "management",
            "changes",
            "managingYour",
            "mixing",
            "influencing",
            "dynamics",
          ])
      );
    });

    const cardLevel = computed(() => {
      if (isGeneral.value || (!props.card?.domain && !props.card?.branch)) {
        return "total";
      }

      if (props.card?.branch || intersects(structureClasses.value, SECTION_CLASSES)) {
        return "section";
      }

      if (intersects(structureClasses.value, BRANCH_CLASSES)) {
        return "branch";
      }

      if (intersects(structureClasses.value, DOMAIN_CLASSES) || props.card?.domain) {
        return "domain";
      }

      return "section";
    });

    const cardTone = computed(() => {
      if (isGeneral.value) {
        return "general";
      }

      if (isExperienced.value) {
        return "experienced";
      }

      if (isStrategic.value) {
        return "strategic";
      }

      return "neutral";
    });

    const isImportant = computed(() => props.card?.important !== false);

    const cardClasses = computed(() => {
      return {
        "reference-parameter-card_important": isImportant.value,
        "reference-parameter-card_unimportant": !isImportant.value,
        [`reference-parameter-card_${cardLevel.value}`]: true,
        [`reference-parameter-card_tone_${cardTone.value}`]: true,
      };
    });

    return {
      cardClasses,
      displayRange,
    };
  },
};
</script>

<style lang="scss" scoped>
.reference-parameter-card {
  --reference-card-border: rgba(0, 0, 0, 0.06);
  --reference-card-background: #ffffff;
  --reference-card-title: rgba(0, 0, 0, 0.88);
  --reference-card-range: rgba(0, 0, 0, 0.88);
  --reference-card-icon: rgba(0, 0, 0, 0.25);

  position: relative;
  width: 100%;
  height: 100%;
  min-height: inherit;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: hidden;
  border-color: var(--reference-card-border);
  background: var(--reference-card-background);
  box-shadow: none !important;
  transition:
      border-color 0.2s ease,
      background-color 0.2s ease,
      color 0.2s ease;

  :deep(.ant-card-body) {
    flex: 1 1 auto;
    min-height: 0;
    padding: 16px !important;
    display: flex;
    flex-direction: column;
  }
}

.reference-parameter-card_tone_general {
  --reference-card-border: rgba(0, 0, 0, 0.06);
  --reference-card-background: #ffffff;
}

.reference-parameter-card_important.reference-parameter-card_tone_experienced.reference-parameter-card_domain {
  --reference-card-border: rgba(162, 155, 254, 0.5);
  --reference-card-background: rgba(208, 204, 255, 0.6);
}

.reference-parameter-card_important.reference-parameter-card_tone_experienced.reference-parameter-card_branch,
.reference-parameter-card_important.reference-parameter-card_tone_experienced.reference-parameter-card_section {
  --reference-card-border: rgba(162, 155, 254, 0.5);
  --reference-card-background: rgba(208, 204, 255, 0.2);
}

.reference-parameter-card_important.reference-parameter-card_tone_strategic.reference-parameter-card_domain {
  --reference-card-border: rgba(113, 229, 224, 0.5);
  --reference-card-background: rgba(180, 255, 252, 0.6);
}

.reference-parameter-card_important.reference-parameter-card_tone_strategic.reference-parameter-card_branch {
  --reference-card-border: rgba(113, 229, 224, 0.5);
  --reference-card-background: rgba(180, 255, 252, 0.2);
}

.reference-parameter-card_important.reference-parameter-card_tone_strategic.reference-parameter-card_section {
  --reference-card-border: rgba(113, 229, 224, 0.5);
  --reference-card-background: rgba(180, 255, 252, 0.2);
}

.reference-parameter-card_unimportant {
  --reference-card-border: #ececec;
  --reference-card-background: #ffffff;
  --reference-card-title: rgba(0, 0, 0, 0.45);
  --reference-card-range: rgba(0, 0, 0, 0.45);
  --reference-card-icon: rgba(0, 0, 0, 0.25);
}

.reference-parameter-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  column-gap: 8px;
}

.reference-parameter-card__title {
  margin-bottom: 12px;
  color: var(--reference-card-title);
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
}

.reference-parameter-card__info {
  flex-shrink: 0;
  margin-top: 1px;
  font-size: 13px;
  color: var(--reference-card-icon);
}

.reference-parameter-card__info_empty {
  opacity: 0.9;
}

.reference-parameter-card__range {
  color: var(--reference-card-range);
  font-weight: 400;
  font-size: 20px;
  line-height: 120%;
}

.reference-parameter-card_branch,
.reference-parameter-card_section {
  .reference-parameter-card__range {
    margin-top: auto;
  }
}

.reference-parameter-card__popover {
  max-width: 280px;
  font-size: 13px;
  line-height: 18px;

  p {
    margin: 0;
  }
}
</style>